import { PHASE_COLORS_HEX } from "./GrowPhaseBar.jsx";

export const EVENT_META = {
  misted:           { icon: "💧", short: "Mist",   label: "Misted",        color: "#3b82f6" },
  fan_adjusted:     { icon: "🌀", short: "Fan",    label: "Fan adjusted",  color: "#8b5cf6" },
  harvested:        { icon: "🧺", short: "Harv",   label: "Harvested",     color: "#10b981" },
  contam_spotted:   { icon: "⚠️", short: "Cont",   label: "Contamination", color: "#ef4444" },
  phase_transition: { icon: "🔄", short: "Phase",  label: "Phase",         color: "#f59e0b" },
  photo_taken:      { icon: "📷", short: "Photo",  label: "Photo",         color: "#6b7280" },
  custom:           { icon: "📌", short: "Event",  label: "Custom",        color: "#9ca3af" },
};

// Returns the set of event_type strings that appear in notes (for chip rendering).
export function activeEventTypes(notes = []) {
  const seen = new Set();
  for (const n of notes) {
    if (n.event_type && EVENT_META[n.event_type]) seen.add(n.event_type);
  }
  return [...seen];
}

// Computes the `marks` prop for MetricChart.
// enabledTypes: Set of event_type strings to show (null = show all).
export function computeGrowMarks(phases = [], notes = [], enabledTypes = null) {
  const now = Date.now();

  // Phase background bands
  const markArea = {
    silent: true,
    data: phases.map((phase) => [
      {
        name: phase.phase_name,
        xAxis: new Date(phase.started_at).getTime(),
        itemStyle: {
          color: PHASE_COLORS_HEX[phase.phase_name] ?? "#6b7280",
          opacity: 0.10,
        },
        label: {
          show: true,
          position: "insideTopLeft",
          fontSize: 10,
          color: "#9ca3af",
          formatter: phase.phase_name,
        },
      },
      { xAxis: phase.ended_at ? new Date(phase.ended_at).getTime() : now },
    ]),
  };

  // Phase boundary lines (dashed gray, no label)
  const phaseBoundaryLines = phases
    .filter((p) => p.ended_at)
    .map((p) => ({
      xAxis: new Date(p.ended_at).getTime(),
      lineStyle: { type: "dashed", color: "#6b7280", width: 1 },
      label: { show: false },
    }));

  // Event lines — vertical solid lines with icon label at top
  const typedNotes = notes.filter((n) => {
    if (!n.event_type || !EVENT_META[n.event_type]) return false;
    if (enabledTypes && !enabledTypes.has(n.event_type)) return false;
    return true;
  });

  // Events created close together in time land on nearly the same x pixel, so their
  // text labels collide and overlap illegibly. Sort by time and mark any event within
  // ~2% of the full range from its neighbor as "clustered" — clustered events fall back
  // to a compact icon-only badge (still color-coded per type) instead of a text label;
  // full detail for every event remains available in the Notes & Events feed below.
  const sortedNotes = [...typedNotes].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  const allTimes = sortedNotes.map((n) => new Date(n.created_at).getTime());
  const rangeMs = allTimes.length > 1 ? Math.max(...allTimes) - Math.min(...allTimes) || 1 : 1;
  const clusterGapMs = rangeMs * 0.02;

  const clustered = new Set();
  for (let i = 1; i < allTimes.length; i++) {
    if (allTimes[i] - allTimes[i - 1] < clusterGapMs) {
      clustered.add(i - 1);
      clustered.add(i);
    }
  }

  const eventLines = sortedNotes.map((n, i) => {
    const meta = EVENT_META[n.event_type];
    const isClustered = clustered.has(i);
    return {
      xAxis: new Date(n.created_at).getTime(),
      lineStyle: { type: "solid", color: meta.color, width: 1.5, opacity: 0.8 },
      label: {
        show: true,
        position: "start",
        distance: 4,
        formatter: isClustered ? meta.icon : meta.short,
        fontSize: isClustered ? 11 : 10,
        fontWeight: "bold",
        color: "#fff",
        backgroundColor: meta.color,
        borderRadius: isClustered ? 999 : 3,
        padding: isClustered ? [2, 3, 2, 3] : [2, 4, 2, 4],
      },
    };
  });

  const markLine = {
    silent: true,
    symbol: "none",
    data: [...phaseBoundaryLines, ...eventLines],
  };

  return { markArea, markLine };
}
